import { message, Pagination, Row, Tag, Tooltip } from "antd";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CommonButton } from "../../components/CommonButton/button";
import ReportsFilterMenu from "../../components/CommonFilter/ReportsFilterMenu";
import { Text } from "../../components/CommonFont/text";
import { SearchBox } from "../../components/CommonSearchBox";
import { CommonTable } from "../../components/commonTable";
import { TableCard } from "../../components/CommonTableCard";
import DateRangeFilter from "../../components/Modals/ReportsModal/DateRangeFilter";
import ExportModal from "../../components/Modals/ReportsModal/ExportModal";
import MultiSelectFilter from "../../components/Modals/ReportsModal/MultiSelectFilter";
import {
  useExportSalesReportMutation,
  useGetEnquiryCategoryQuery,
  useGetExecutivesFilterQuery,
  useGetQuotationProductsDropdownQuery,
  useGetQuotationsReportQuery,
  useGetSalesProductDropdownItemsQuery,
  useGetSalesReportQuery,
} from "../../services/api";
import "./index.css";
import {
  getDefaultQuotationSelectedColumns,
  getDefaultSelectedColumns,
  QuotationColumnLabels,
  QuotationTableColumn,
  ReportColumn,
  ReportColumnLabels,
  SalesReportItem,
  statusFilterOptions,
} from "./reportColumns";

type ColumnKey = ReportColumn;

// Tab types
type TabType = "orders" | "quotations";

type Product = {
  code?: string;
  name?: string;
  color?:string;
};

export const Reports = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>("orders");
  const [isOrdersTab, setIsOrdersTab] = useState(true);
  const [isQuotationsTab, setIsQuotationsTab] = useState(false);

  const [dateRange, setDateRange] = useState<any>(null);
  const isManager = localStorage.getItem("user_role");

  const [exportStatusFilter, setExportStatusFilter] = useState<string[]>([]);

  // This state for export columns selection
  const [selectedColumns, setSelectedColumns] = useState<
    Record<string, boolean>
  >(getDefaultSelectedColumns());

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pageData, setPageData] = useState<{
    page: number;
    pageSize: number;
  }>({
    page: 1,
    pageSize: 10,
  });

  const [searchParams] = useSearchParams();
  const search = searchParams.get("search") || "";

  // Function to get params
  const getReportsParams = (page: number, pageSize: number) => {
    return {
      page: page,
      page_size: pageSize,
    };
  };

  const params = getReportsParams(pageData.page, pageData.pageSize);

  // Filter states for Orders tab
  const [filter, setFilter] = useState(false);
  const [tempExecutiveIds, setTempExecutiveIds] = useState<number[]>([]);
  const [executiveIds, setExecutiveIds] = useState<number[]>([]);
  const [tempProductIds, setTempProductIds] = useState<number[]>([]);
  const [productIds, setProductIds] = useState<number[]>([]);
  const [exportProductIds, setExportProductIds] = useState<number[]>([]);
  const [exportExecutiveIds, setExportExecutiveIds] = useState<number[]>([]);
  const [exportQuotationProductIds, setExportQuotationProductIds] = useState<number[]>([]);

  // Filter states for Quotations tab
  const [tempEnquiryCategoryIds, setTempEnquiryCategoryIds] = useState<
    number[]
  >([]);
  const [enquiryCategoryIds, setEnquiryCategoryIds] = useState<number[]>([]);
  const [tempStatusFilter, setTempStatusFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);

  // Export state
  const [exportSalesReport, { isLoading: isExporting }] =
    useExportSalesReportMutation();

  // For executives - depends on selected products for export
  const executiveFilterParams = {
    ...(exportProductIds.length > 0 && {
      finished_goods_id: exportProductIds.join(","),
    }),
  };

  // For products - depends on selected executives for export
  const productFilterParams = {
    ...(exportExecutiveIds.length > 0 && {
      executive_id: exportExecutiveIds.join(","),
    }),
  };

  // Fetch executives data for the modal filter
  const { data: executivesData, refetch: refetchExecutives } =
    useGetExecutivesFilterQuery(executiveFilterParams);
  const { data: salesDropdownData, refetch: refetchProducts } =
    useGetSalesProductDropdownItemsQuery(productFilterParams);
  const { data: quotationProductsData } = useGetQuotationProductsDropdownQuery({});
  const { data: enquiryCategoryData } = useGetEnquiryCategoryQuery({});

  // Refetch when dependent filters change
  useEffect(() => {
    refetchExecutives();
  }, [exportProductIds]);

  useEffect(() => {
    refetchProducts();
  }, [exportExecutiveIds]);

  // Build query parameters object for Orders tab
  const getAllReportsParams = {
    params: params,
    search: search ? search : "",
    executive_ids: executiveIds,
    product_ids: productIds,
  };

  // Build query parameters for Quotations tab
  const getAllQuotationsParams = {
    params: params,
    search: search ? search : "",
    executive_ids: executiveIds,
    product_ids: productIds,
    enquiry_category_ids: enquiryCategoryIds,
    is_closed: statusFilter.length === 1 ? (statusFilter[0] === "false" ? 0 : 1) : undefined,
  };

  // Fetch data from API for Orders tab
  const {
    data: ordersData,
    isLoading: ordersLoading,
    isFetching: ordersFetching,
  } = useGetSalesReportQuery(getAllReportsParams, {
    skip: activeTab !== "orders",
  });

  // Fetch data from API for Quotations tab
  const {
    data: quotationsData,
    isLoading: quotationsLoading,
    isFetching: quotationsFetching,
    refetch: refetchQuotations,
  } = useGetQuotationsReportQuery(getAllQuotationsParams, {
    skip: activeTab !== "quotations",
  });

  useEffect(() => {
    if (activeTab === "quotations") {
      refetchQuotations();
    }
  }, [statusFilter]);

  const showModal = () => {
    setIsModalOpen(true);
  };

  // Reset all modal filters when closing
  const handleCancel = () => {
    // Reset date range
    setDateRange(null);

    // Reset executive selection (only for managers)
    if (isManager === "1") {
      setExecutiveIds([]);
      setTempExecutiveIds([]);
      setExportExecutiveIds([]);
    }

    setProductIds([]);
    setTempProductIds([]);
    setExportProductIds([]);
    setExportQuotationProductIds([]);

    setEnquiryCategoryIds([]);
    setTempEnquiryCategoryIds([]);
    setStatusFilter([]);
    setTempStatusFilter([]);
    setExportStatusFilter([]);

    // Reset columns based on active tab
    if (activeTab === "orders") {
      setSelectedColumns(getDefaultSelectedColumns());
    } else {
      setSelectedColumns(getDefaultQuotationSelectedColumns());
    }

    // Close modal
    setIsModalOpen(false);
  };

  const onPaginationChange = (page: number, pageSize: number) => {
    setPageData({ page, pageSize });
  };

  const onShowSizeChange = (current: number, pageSize: number) => {
    setPageData({ page: 1, pageSize });
  };

  // Handle tab change
  const handleOrdersTabClick = () => {
    setActiveTab("orders");
    setIsOrdersTab(true);
    setIsQuotationsTab(false);
    setSelectedColumns(getDefaultSelectedColumns());
    // Reset to first page when changing tabs
    setPageData({ page: 1, pageSize: pageData.pageSize });
  };

  const handleQuotationsTabClick = () => {
    setActiveTab("quotations");
    setIsOrdersTab(false);
    setIsQuotationsTab(true);
    setSelectedColumns(getDefaultQuotationSelectedColumns());
    // Reset to first page when changing tabs
    setPageData({ page: 1, pageSize: pageData.pageSize });
  };

  // Heading data for TableCard (mobile view) - Orders tab
  const ordersTitleData = [
    ReportColumnLabels[ReportColumn.ORDER_NO],
    ReportColumnLabels[ReportColumn.EXECUTIVE_NAME],
    ReportColumnLabels[ReportColumn.LEAD_NAME],
    ReportColumnLabels[ReportColumn.COMPANY_NAME],
    ReportColumnLabels[ReportColumn.ENQUIRY_CATEGORY],
    ReportColumnLabels[ReportColumn.ADVANCE_RECEIVED_AMOUNT],
    ReportColumnLabels[ReportColumn.ORDER_DATE],
    ReportColumnLabels[ReportColumn.ORDER_STATUS_DISPLAY],
  ];

  // Current title data based on active tab
  const currentTitleData = activeTab === "orders" ? ordersTitleData : [];

  const truncateWithTooltip = (text: any, maxLength: number = 50) => {
    if (!text) return "-";
    if (typeof text !== "string") return text;
    if (text.length <= maxLength) return text;
    return <Tooltip title={text}>{`${text.slice(0, maxLength)}...`}</Tooltip>;
  };

  const formatProduct = (product: Product): string => {
    if (!product || typeof product !== "object") return "";

    const { code, name, color } = product;

    return [code, name, color].filter(Boolean).join(" - ") || "";
  };

  const renderProductsCell = (products: Product | Product[] | string) => {
    const displayText = Array.isArray(products)
      ? products.map(formatProduct).filter(Boolean).join(", ")
      : products && typeof products === "object"
        ? formatProduct(products)
        : String(products ?? "");

    return truncateWithTooltip(displayText || "-", 50);
  };

  // Define columns for Orders tab
  const ordersColumns = [
    {
      key: "orderNumber",
      title: ReportColumnLabels[ReportColumn.ORDER_NO],
      sorter: (a: SalesReportItem, b: SalesReportItem) =>
        (a.order_no || "").localeCompare(b.order_no || ""),
      dataIndex: ReportColumn.ORDER_NO,
      ellipsis: true,
    },
    ...(isManager === "1"
      ? [
          {
            key: "executiveName",
            title: ReportColumnLabels[ReportColumn.EXECUTIVE_NAME],
            dataIndex: ReportColumn.EXECUTIVE_NAME,
            sorter: (a: SalesReportItem, b: SalesReportItem) =>
              (a.executive_name || "").localeCompare(b.executive_name || ""),
            ellipsis: true,
          },
        ]
      : []),
    {
      key: "leadName",
      title: ReportColumnLabels[ReportColumn.LEAD_NAME],
      dataIndex: ReportColumn.LEAD_NAME,
      sorter: (a: SalesReportItem, b: SalesReportItem) =>
        (a.lead_name || "").localeCompare(b.lead_name || ""),
      ellipsis: true,
    },
    {
      key: "companyName",
      title: ReportColumnLabels[ReportColumn.COMPANY_NAME],
      dataIndex: ReportColumn.COMPANY_NAME,
      sorter: (a: SalesReportItem, b: SalesReportItem) =>
        (a.company_name || "").localeCompare(b.company_name || ""),
      ellipsis: true,
    },
    {
      key: "products",
      title: ReportColumnLabels[ReportColumn.PRODUCTS_NAME],
      dataIndex: ReportColumn.PRODUCTS_NAME,
      ellipsis: true,
      render: renderProductsCell,
    },
    {
      key: "enquiryCategory",
      title: ReportColumnLabels[ReportColumn.ENQUIRY_CATEGORY],
      dataIndex: ReportColumn.ENQUIRY_CATEGORY,
      ellipsis: true,
    },
    {
      key: "advanceReceived",
      title: ReportColumnLabels[ReportColumn.ADVANCE_RECEIVED_AMOUNT],
      dataIndex: ReportColumn.ADVANCE_RECEIVED_AMOUNT,
      ellipsis: true,
      render: (value: number | null) => {
        if (value) {
          return `₹${value.toLocaleString()}`;
        }
        return "-";
      },
    },
    {
      key: "orderDate",
      title: ReportColumnLabels[ReportColumn.ORDER_DATE],
      dataIndex: ReportColumn.ORDER_DATE,
      sorter: (a: SalesReportItem, b: SalesReportItem) =>
        (a.order_date || "").localeCompare(b.order_date || ""),
      ellipsis: true,
      render: (value: string) => {
        if (value) {
          return dayjs(value).format("DD-MM-YYYY");
        }
        return "-";
      },
    },
    {
      key: "status",
      title: ReportColumnLabels[ReportColumn.ORDER_STATUS_DISPLAY],
      dataIndex: ReportColumn.ORDER_STATUS_DISPLAY,
      ellipsis: true,
      render: (text: string) => {
        const statusColors: Record<string, string> = {
          "Dispatch Added": "green",
          Pending: "orange",
          Completed: "blue",
          Cancelled: "red",
          "In Progress": "lime",
          Delivered: "cyan",
        };
        const color = statusColors[text] || "default";
        return <Tag color={color}>{text || "-"}</Tag>;
      },
    },
  ];

  // Define columns for Quotations tab
  const quotationsColumns = [
    {
      key: "createdDate",
      title: QuotationColumnLabels[QuotationTableColumn.CREATED_DATE],
      dataIndex: QuotationTableColumn.CREATED_DATE,
      ellipsis: true,
      render: (value: string) => {
        if (value) {
          return dayjs(value).format("DD-MM-YYYY");
        }
        return "-";
      },
    },
    {
      key: "cuiNo",
      title: QuotationColumnLabels[QuotationTableColumn.CUI_NO],
      dataIndex: QuotationTableColumn.CUI_NO,
      ellipsis: true,
    },
    ...(isManager === "1"
      ? [
          {
            key: "executiveName",
            title: QuotationColumnLabels[QuotationTableColumn.EXECUTIVE_NAME],
            dataIndex: QuotationTableColumn.EXECUTIVE_NAME,
            ellipsis: true,
          },
        ]
      : []),
    {
      key: "salesLeadId",
      title: QuotationColumnLabels[QuotationTableColumn.SALES_LEAD_ID],
      dataIndex: QuotationTableColumn.SALES_LEAD_ID,
      ellipsis: true,
    },
    {
      key: "leadName",
      title: QuotationColumnLabels[QuotationTableColumn.LEAD_NAME],
      dataIndex: QuotationTableColumn.LEAD_NAME,
      ellipsis: true,
    },
    {
      key: "companyName",
      title: QuotationColumnLabels[QuotationTableColumn.COMPANY_NAME],
      dataIndex: QuotationTableColumn.COMPANY_NAME,
      ellipsis: true,
    },
    {
      key: "email",
      title: QuotationColumnLabels[QuotationTableColumn.EMAIL],
      dataIndex: QuotationTableColumn.EMAIL,
      ellipsis: true,
    },
    {
      key: "phone",
      title: QuotationColumnLabels[QuotationTableColumn.PHONE],
      dataIndex: QuotationTableColumn.PHONE,
      ellipsis: true,
    },
    {
      key: "product",
      title: QuotationColumnLabels[QuotationTableColumn.PRODUCT],
      dataIndex: QuotationTableColumn.PRODUCT,
      ellipsis: true,
      render: renderProductsCell,
    },
    {
      key: "enquiryCategory",
      title: QuotationColumnLabels[QuotationTableColumn.ENQUIRY_CATEGORY_NAME],
      dataIndex: QuotationTableColumn.ENQUIRY_CATEGORY_NAME,
      ellipsis: true,
    },
    {
      key: "lastFollowupDate",
      title: QuotationColumnLabels[QuotationTableColumn.LAST_FOLLOWUP_DATE],
      dataIndex: QuotationTableColumn.LAST_FOLLOWUP_DATE,
      ellipsis: true,
      render: (value: string) => {
        if (value) {
          return dayjs(value).format("DD-MM-YYYY");
        }
        return "-";
      },
    },
    {
      key: "status",
      title: QuotationColumnLabels[QuotationTableColumn.STATUS],
      dataIndex: QuotationTableColumn.STATUS,
      ellipsis: true,
      render: (text: string) => {
        const isClosed = text === "Closed";
        return <Tag color={isClosed ? "red" : "green"}>{text || "-"}</Tag>;
      },
    },
  ];

  // Column options based on active tab
  const columnOptions = (
    activeTab === "orders" ? ordersColumns : quotationsColumns
  ).map((col) => ({
    dataIndex: col.dataIndex,
    title: col.title,
    key: col.key,
  }));

  // Force executive_name to false for non-managers in selectedColumns
  useEffect(() => {
    if (isManager !== "1") {
      setSelectedColumns((prev) => ({
        ...prev,
        [ReportColumn.EXECUTIVE_NAME]: false,
      }));
    }
  }, [isManager]);

  // Reset to first page whenever search changes
  useEffect(() => {
    setPageData((prev) => ({
      ...prev,
      page: 1,
    }));
  }, [search]);

  const getSelectedColumnsArray = () => {
    let filteredColumns = columnOptions.filter(
      (col) => selectedColumns[col.dataIndex as ColumnKey],
    );

    // If not manager, NEVER include executive_name in export
    if (isManager !== "1") {
      filteredColumns = filteredColumns.filter(
        (col) => col.dataIndex !== ReportColumn.EXECUTIVE_NAME,
      );
    }

    // Map table column names to export column names
    return filteredColumns.map((col) => {
      // For quotations tab, map 'product' to 'products' for backend
      if (activeTab === "quotations" && col.dataIndex === "product") {
        return "products";
      }
      return col.dataIndex;
    });
  };

  const handleDownload = async () => {
    // Validate at least one column is selected
    const selectedColumnsArray = getSelectedColumnsArray();
    if (selectedColumnsArray.length === 0) {
      message.error("Please select at least one column to export");
      return;
    }

    try {
      // Build export payload based on active tab
      const exportPayload: any = {
        module: activeTab === "orders" ? "sales_report" : "sales_report_quotation",
        columns: selectedColumnsArray,
      };

      // date range if selected
      if (dateRange && dateRange[0] && dateRange[1]) {
        exportPayload.start_date = dateRange[0].format("YYYY-MM-DD");
        exportPayload.end_date = dateRange[1].format("YYYY-MM-DD");
      }

      // For Orders tab only - executive IDs if selected (only for managers)
      if (
        activeTab === "orders" &&
        isManager === "1" &&
        exportExecutiveIds &&
        exportExecutiveIds.length > 0
      ) {
        exportPayload.executive_id = exportExecutiveIds;
      }

      // For Orders tab only - product IDs if selected
      if (
        activeTab === "orders" &&
        exportProductIds &&
        exportProductIds.length > 0
      ) {
        exportPayload.product_id = exportProductIds;
      }

      // For Quotations tab - product IDs if selected
      if (
        activeTab === "quotations" &&
        exportQuotationProductIds &&
        exportQuotationProductIds.length > 0
      ) {
        exportPayload.product_id = exportQuotationProductIds;
      }

      // For Quotations tab - enquiry category IDs
      if (activeTab === "quotations" && enquiryCategoryIds.length > 0) {
        exportPayload.enquiry_category_id = enquiryCategoryIds;
      }

      // For Quotations tab - status filter
      if (activeTab === "quotations" && exportStatusFilter.length > 0 && exportStatusFilter.length === 1) {
        exportPayload.is_closed =
          exportStatusFilter[0] === "true" ? 0 : 1;
      }

      const response = await exportSalesReport(exportPayload).unwrap();

      if (response.file_url) {
        message.success(response.message || "Export completed successfully");
        const fileResponse = await fetch(response.file_url);
        const blob = await fileResponse.blob();
        saveAs(blob, response.file_name);
        handleCancel();
      } else {
        message.error(response.message || "Export failed. Please try again.");
      }
    } catch (error: any) {
      console.error("Export error:", error);
      if (error?.data) {
        message.error(error.data.message || "Export failed. Please try again.");
      } else {
        message.error("Something went wrong. Please try again.");
      }
    }
  };

  // Handle filter button click - populate temp states
  const handleFilterClick = () => {
    setTempExecutiveIds(executiveIds);
    setTempProductIds(productIds);
    if (activeTab === "quotations") {
      setTempEnquiryCategoryIds(enquiryCategoryIds);
      setTempStatusFilter(statusFilter);
    }
    setFilter(!filter);
  };

  // Table columns based on active tab
  // Current data, columns, and loading based on active tab
  const currentData = activeTab === "orders" ? ordersData : quotationsData;
  const currentColumns =
    activeTab === "orders" ? ordersColumns : quotationsColumns;
  const currentLoading =
    activeTab === "orders" ? ordersLoading : quotationsLoading;
  const currentFetching =
    activeTab === "orders" ? ordersFetching : quotationsFetching;
  const totalCount = currentData?.count || 0;

  return (
    <>
      <div className="mt-24">
        <div className="flex justify-between items-center">
          <span className="sales-title">
            <Text value="Reports" variant="wearlay-display" />
          </span>
        </div>

        {/* Tabs Section - Desktop */}
        <div className="order-tab-main-div justify-end mt-24">
          <div
            className={isOrdersTab ? "order-tabs-active" : "order-tabs"}
            onClick={handleOrdersTabClick}
          >
            Orders
          </div>
          <div
            className={isQuotationsTab ? "order-tabs-active" : "order-tabs"}
            onClick={handleQuotationsTabClick}
          >
            Quotations
          </div>
        </div>

        {/* Tabs Section - Mobile Responsive */}
        <div className="order-tab-main-div-2 mt-16">
          <div className="flex m-inline-24">
            <div
              className={isOrdersTab ? "order-tabs-active" : "order-tabs"}
              onClick={handleOrdersTabClick}
            >
              Orders
            </div>
            <div
              className={isQuotationsTab ? "order-tabs-active" : "order-tabs"}
              onClick={handleQuotationsTabClick}
            >
              Quotations
            </div>
          </div>
        </div>

        <div className="p-10 mt-24p">
          <div className="flex justify-between">
            <Text
              value={
                activeTab === "orders" ? "Reports List" : "Quotations List"
              }
              variant="wearlay-headline-2"
              className="p-1 "
            />

            <div className="flex items-center h-full gap-2">
              <div className="search-resp ">
                <SearchBox placeholder="Search" />
              </div>

              {/* Filter Button - Only show for Orders tab */}

              <div className="relative">
                <CommonButton
                  variant="wearlay-with-icon"
                  icon={filter ? "/assets/close.svg" : "/assets/filter.svg"}
                  onClick={handleFilterClick}
                  className="download-btn-color"
                />
                {filter && (
                  <ReportsFilterMenu
                    setFilter={setFilter}
                    setExecutiveIds={setExecutiveIds}
                    setProductIds={setProductIds}
                    setEnquiryCategoryIds={setEnquiryCategoryIds}
                    setStatusFilter={setStatusFilter}
                    filter={filter}
                    selectedExecutiveIds={tempExecutiveIds}
                    setSelectedExecutiveIds={setTempExecutiveIds}
                    selectedProductIds={tempProductIds}
                    setSelectedProductIds={setTempProductIds}
                    selectedEnquiryCategoryIds={tempEnquiryCategoryIds}
                    setSelectedEnquiryCategoryIds={setTempEnquiryCategoryIds}
                    selectedStatusFilter={tempStatusFilter}
                    setSelectedStatusFilter={setTempStatusFilter}
                    activeTab={activeTab}
                  />
                )}
              </div>

              <CommonButton
                variant="wearlay-with-icon"
                icon={
                  totalCount === 0
                    ? "/assets/disabledDownload.svg"
                    : "/assets/download.svg"
                }
                onClick={showModal}
                className="download-btn-color"
                disabled={totalCount === 0}
              />
            </div>
          </div>
          <div className="resp-search">
            <SearchBox placeholder="Search" />
          </div>

          {/* Desktop Table View */}
          <div className="resp-report-table">
            <CommonTable
              datasource={currentData?.results}
              coloumn={currentColumns}
              isLoading={currentLoading || currentFetching}
            />
            {totalCount > 0 && (
              <Row justify={"end"} className="mt-16">
                <Pagination
                  current={pageData.page}
                  onChange={onPaginationChange}
                  total={totalCount}
                  pageSize={pageData.pageSize}
                  defaultCurrent={1}
                  responsive={true}
                  onShowSizeChange={onShowSizeChange}
                  showSizeChanger={totalCount > 10 ? true : false}
                />
              </Row>
            )}
          </div>

          {/* Mobile Card View */}
          <TableCard
            headingData={currentTitleData}
            data={currentData?.results}
            loading={currentLoading || currentFetching}
          />
          {totalCount > 0 && (
            <Pagination
              current={pageData.page}
              onChange={onPaginationChange}
              total={totalCount}
              pageSize={pageData.pageSize}
              className="pagination-resp"
              defaultCurrent={1}
              onShowSizeChange={onShowSizeChange}
              showSizeChanger={totalCount > 10}
            />
          )}
        </div>
      </div>

      <ExportModal
        isModalOpen={isModalOpen}
        handleCancel={() => {
          setIsModalOpen(false);
          handleCancel();
        }}
        title={activeTab === "orders" ? "Sales Report" : "Quotations Export"}
        columnOptions={columnOptions}
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
        handleDownload={handleDownload}
        isExporting={isExporting}
      >
        {/* Date Range Filter - Common for both tabs */}
        <DateRangeFilter dateRange={dateRange} setDateRange={setDateRange} />

        {/* Executive Filter - Only for Orders tab and managers */}
        {isManager === "1" && (
          <MultiSelectFilter
            value={exportExecutiveIds}
            onChange={setExportExecutiveIds}
            options={executivesData?.results || []}
            label="Executive"
            placeholder="Select executives"
            optionLabelKey="full_name"
            optionValueKey="id"
          />
        )}

        {/* Product Filter - Different data source based on active tab */}
        {activeTab === "orders" && (
          <MultiSelectFilter
            value={exportProductIds}
            onChange={setExportProductIds}
            options={salesDropdownData?.data || []}
            label="Product"
            placeholder="Select Products"
            optionLabelKey="name"
            optionValueKey="id"
          />
        )}
        {activeTab === "quotations" && (
          <MultiSelectFilter
            value={exportQuotationProductIds}
            onChange={setExportQuotationProductIds}
            options={quotationProductsData?.data || []}
            label="Product"
            placeholder="Select Products"
            optionLabelKey="name"
            optionValueKey="id"
          />
        )}

        {activeTab === "quotations" && (
          <MultiSelectFilter
            value={enquiryCategoryIds}
            onChange={setEnquiryCategoryIds}
            options={enquiryCategoryData || []}
            label="Enquiry Category"
            placeholder="Select enquiry categories"
            optionLabelKey="name"
            optionValueKey="id"
          />
        )}
        {activeTab === "quotations" && (
          <MultiSelectFilter
            value={exportStatusFilter}
            onChange={setExportStatusFilter}
            options={statusFilterOptions}
            label="Status"
            placeholder="Select status"
          />
        )}
      </ExportModal>
    </>
  );
};

export default Reports;
